import { supabase } from '@/services/supabase/client';
import type { Product, ProductRecommendation } from '@/types';
import type { Capability, AiCapability } from '@/types/database';

export async function getProducts() {
  const { data, error } = await supabase
    .from('products')
    .select('*')
    .eq('is_active', true)
    .order('sort_order');

  if (error) throw error;
  return data as Product[];
}

export async function getProductBySlug(slug: string) {
  const { data, error } = await supabase
    .from('products')
    .select('*')
    .eq('slug', slug)
    .eq('is_active', true)
    .maybeSingle();

  if (error) throw error;
  return data as Product | null;
}

export async function getProductCapabilities(productId: string) {
  const { data, error } = await supabase
    .from('product_capabilities')
    .select('capability_id, capabilities(*)')
    .eq('product_id', productId);

  if (error) throw error;
  return (data ?? [])
    .map((row) => (row as unknown as { capabilities: Capability }).capabilities)
    .filter(Boolean);
}

export async function getProductAiCapabilities(productId: string) {
  const { data, error } = await supabase
    .from('product_ai_capabilities')
    .select('ai_capability_id, ai_capabilities(*)')
    .eq('product_id', productId);

  if (error) throw error;
  return (data ?? [])
    .map((row) => (row as unknown as { ai_capabilities: AiCapability }).ai_capabilities)
    .filter(Boolean);
}

export async function getRelatedProducts(productId: string) {
  const { data: capLinks, error: capError } = await supabase
    .from('product_capabilities')
    .select('capability_id')
    .eq('product_id', productId);

  if (capError) throw capError;
  if (!capLinks || capLinks.length === 0) return [];

  const capIds = capLinks.map((l) => l.capability_id);

  const { data: relatedLinks, error: relError } = await supabase
    .from('product_capabilities')
    .select('product_id')
    .in('capability_id', capIds)
    .neq('product_id', productId);

  if (relError) throw relError;
  if (!relatedLinks || relatedLinks.length === 0) return [];

  const uniqueProductIds = [...new Set(relatedLinks.map((l) => l.product_id))];

  const { data: products, error: prodError } = await supabase
    .from('products')
    .select('*')
    .in('id', uniqueProductIds)
    .eq('is_active', true)
    .order('sort_order');

  if (prodError) throw prodError;
  return products as Product[];
}

export async function getProductComparison(slugs: string[]) {
  const { data, error } = await supabase
    .from('products')
    .select('*')
    .in('slug', slugs)
    .eq('is_active', true)
    .order('sort_order');

  if (error) throw error;
  return data as Product[];
}

export async function getRecommendationsForProblem(problemId: string) {
  const { data, error } = await supabase
    .from('product_recommendations')
    .select('*, products(*)')
    .eq('business_problem_id', problemId)
    .order('confidence_score', { ascending: false });

  if (error) throw error;
  return data as (ProductRecommendation & { products: Product })[];
}

export async function recordProductEvent(
  productId: string,
  eventType: string,
  sessionId?: string
) {
  const { error } = await supabase
    .from('product_analytics')
    .insert({
      product_id: productId,
      event_type: eventType,
      session_id: sessionId ?? null,
    });

  if (error) throw error;
}

export async function getProductStats(productId: string) {
  const { data, error } = await supabase
    .from('product_analytics')
    .select('event_type')
    .eq('product_id', productId);

  if (error) throw error;

  const events = data ?? [];
  return {
    views: events.filter((e) => e.event_type === 'view').length,
    demoClicks: events.filter((e) => e.event_type === 'demo_click').length,
    askSanjoyClicks: events.filter((e) => e.event_type === 'ask_sanjoy_click').length,
  };
}
